import { CHARACTER_KEYS, SINGER_REF, thisYear } from './consts';
import { clampNum, mean, median, percentile } from './math';
import { computeMetrics, scoreSong } from './metrics';
import { noteName } from './note';
import { isValidVoice } from './pitch';

import type {
  CharacterKey,
  ClipSet,
  FachKey,
  MetricsInput,
  Placement,
  Registers,
  Report,
  ServerAnalysis,
  SurveyAnswers,
  VocalAge,
} from './types';

export const classifyFach = (
  gender: string | undefined,
  lowF: number | null,
  highF: number | null,
): FachKey | null => {
  if (!lowF || !highF) return null;
  const mid = Math.sqrt(lowF * highF);
  // Match language-neutral ids (see SURVEY gender optValues), not localized
  // display text — survey copy is i18n'd, so '여'/'남' substrings vanish in EN.
  // Returns language-neutral fach keys; apps localize via i18n.
  if (gender === 'female') return mid >= 370 ? 'soprano' : 'alto';
  if (gender === 'male') return mid >= 200 ? 'tenor' : 'bass';
  return mid >= 300 ? 'soprano_tenor' : 'alto_bass';
};

/**
 * 출생연도 → 나이(올해 − 출생연도, 연 나이). 생일을 모르므로 만 나이는 한 살 틀릴 수 있다 —
 * 틀린 정밀도보다 한 가지 산식이 낫다: 음성 나이 비교(`estimateVocalAge`)와 프로필 화면의
 * "나이"가 같은 값을 말한다. 출생연도가 없으면 null(0살이 아니다).
 */
export const ageFromBirthYear = (
  birthYear: string | number | undefined,
  currentYear: number = thisYear(),
): number | null => (birthYear ? currentYear - Number(birthYear) : null);

export const estimateVocalAge = (
  birthYear: string | number | undefined,
  hnrMean: number,
  stability: number,
  currentYear: number = thisYear(),
): VocalAge => {
  const actual = ageFromBirthYear(birthYear, currentYear);
  const quality = clampNum(hnrMean * 5 + stability * 0.3, 0, 100);
  let voiceAge =
    actual != null
      ? Math.round(actual + (50 - quality) / 8)
      : Math.round(45 - quality * 0.4);
  voiceAge = Math.max(14, Math.min(70, voiceAge));
  return { voiceAge, actual };
};

export const vocalCharacter = (
  hnrMean: number,
  centroid: number,
): CharacterKey => {
  if (hnrMean < 3.5) return 'breathy';
  if (centroid > 2700) return 'clear';
  if (centroid > 2000) return 'bright';
  if (centroid > 1400) return 'balanced';
  return 'warm';
};

export const registerRatio = (
  medF: number,
  lowF: number | null,
  highF: number | null,
  centroid: number,
): Registers => {
  let pos = 0.5;
  if (lowF && highF && highF > lowF) {
    pos = clampNum(Math.log2(medF / lowF) / Math.log2(highF / lowF), 0, 1);
  }
  const bright = clampNum((centroid - 1200) / 2200, 0, 1);
  let head = clampNum((pos * 0.6 + bright * 0.4) * 100, 5, 88);
  let chest = clampNum((100 - head) * 0.7, 5, 88);
  let mix = Math.max(5, 100 - head - chest);
  const s = head + chest + mix;
  head = Math.round((head / s) * 100);
  chest = Math.round((chest / s) * 100);
  mix = 100 - head - chest;
  return { chest, mix, head };
};

/**
 * Client-side articulation heuristic (mirrors muzly-web's demo placement warnings).
 * Object param: derived 0-100 scores + the timbre descriptor. Backend merge can override.
 */
export const derivePlacement = (m: {
  singersFormant: number;
  resonance: number;
  head: number;
  stability: number;
  character: CharacterKey;
  centroid: number;
}): Placement => ({
  velumWarn: m.singersFormant < 45 || m.head < 20,
  foldWarn: m.stability < 55 || m.character === 'pressed',
  tongueWarn: m.centroid < 1300,
  resonanceScore: Math.round(m.resonance),
});

export const matchSingers = (fach: FachKey | null): string[] => {
  if (!fach) return [];
  for (const k in SINGER_REF) if (fach.indexOf(k) >= 0) return SINGER_REF[k];
  return [];
};

/**
 * 음역 끝을 잡는 퍼센타일 — 저음 시도는 P12, 고음 시도는 P88. 테이크별 값(`rangeHz`)과
 * 리포트가 같은 선을 써야 해서 여기 한 곳에 둔다.
 */
export const RANGE_PERCENTILE = { low: 12, high: 88 } as const;

/**
 * Build the full diagnosis report from the four stage clips + survey answers.
 * `currentYear` only feeds the age estimate — pass it to keep a replay deterministic.
 */
export const buildReport = (
  clips: ClipSet,
  survey: SurveyAnswers,
  currentYear: number = thisYear(),
): Report | null => {
  const sustain = clips.sustain;
  if (!isValidVoice(sustain)) return null;

  const input: MetricsInput = {
    pitches: sustain.pitches,
    hnrs: sustain.hnrs,
    centroids: sustain.centroids,
    sfs: sustain.singersFormant || [],
    onsetT: 0,
    lastVoiceT: sustain.voicedSec * 1000,
    vibrato: sustain.vibrato,
  };
  const base = computeMetrics(input);

  const { low, high, song } = clips;
  const lowF =
    low && low.pitches.length >= 4
      ? percentile(low.pitches, RANGE_PERCENTILE.low)
      : null;
  const highF =
    high && high.pitches.length >= 4
      ? percentile(high.pitches, RANGE_PERCENTILE.high)
      : null;
  const range =
    lowF && highF
      ? {
          lowNote: noteName(lowF),
          highNote: noteName(highF),
          lowHz: Math.round(lowF),
          highHz: Math.round(highF),
          semitones: Math.round(12 * Math.log2(highF / lowF)),
        }
      : base.range;

  const hnrMean = mean(sustain.hnrs) || 0;
  const centroidMean = mean(sustain.centroids) || 0;
  const medF = median(sustain.pitches);
  const pitchStab = base.metrics[1] ? base.metrics[1].val : 50;
  const fach = classifyFach(survey.gender, lowF, highF);
  const character = vocalCharacter(hnrMean, centroidMean);
  const registers = registerRatio(medF, lowF, highF, centroidMean);
  const placement = derivePlacement({
    singersFormant: base.singersFormant,
    resonance: base.metrics[4] ? base.metrics[4].val : 50,
    head: registers.head,
    stability: pitchStab,
    character,
    centroid: centroidMean,
  });

  return {
    ...base,
    range,
    _lowF: lowF ?? undefined,
    _highF: highF ?? undefined,
    songScore: song ? scoreSong(song.pitches) : null,
    fach,
    vocalAge: estimateVocalAge(
      survey.birthYear,
      hnrMean,
      pitchStab,
      currentYear,
    ),
    character,
    registers,
    placement,
    singers: matchSingers(fach),
    f3: null,
    f4: null,
  };
};

/** Numeric source keys on ServerAnalysis copied verbatim into the report. */
type PrecisionSrc =
  | 'f0_median_hz'
  | 'spr_db'
  | 'singers_formant_db'
  | 'cpps_db'
  | 'h1h2_db'
  | 'singers_formant_score';
/** Their numeric target keys on Report (each accepts a `number`). */
type PrecisionDst =
  | 'f0median'
  | 'spr'
  | 'singersFormantDb'
  | 'cpps'
  | 'h1h2'
  | 'singersFormant';

/** Scalar precision fields: ServerAnalysis source key → Report target key. */
const PRECISION_FIELDS: ReadonlyArray<[PrecisionSrc, PrecisionDst]> = [
  ['f0_median_hz', 'f0median'],
  ['spr_db', 'spr'],
  ['singers_formant_db', 'singersFormantDb'],
  ['cpps_db', 'cpps'],
  ['h1h2_db', 'h1h2'],
  ['singers_formant_score', 'singersFormant'],
];

/** Merge the scalar precision metrics (LTAS / cepstrum / F0). Returns whether anything changed. */
const mergePrecisionScalars = (report: Report, a: ServerAnalysis): boolean => {
  let changed = false;
  for (const [src, dst] of PRECISION_FIELDS) {
    const v = a[src];
    if (v != null) {
      report[dst] = v;
      changed = true;
    }
  }
  return changed;
};

/** Overlay server placement flags onto the client heuristic (server wins per-field). */
const mergePlacement = (report: Report, a: ServerAnalysis): boolean => {
  const p = a.placement;
  if (!p) return false;
  const present =
    p.velum_warn != null ||
    p.fold_warn != null ||
    p.tongue_warn != null ||
    p.resonance_score != null ||
    p.clarity_cpps != null;
  if (!present) return false;
  const cur = report.placement;
  report.placement = {
    velumWarn: p.velum_warn ?? cur.velumWarn,
    foldWarn: p.fold_warn ?? cur.foldWarn,
    tongueWarn: p.tongue_warn ?? cur.tongueWarn,
    resonanceScore: p.resonance_score ?? cur.resonanceScore,
    clarityCpps: p.clarity_cpps ?? cur.clarityCpps,
  };
  return true;
};

/** Merge the Python precision-backend analysis into a report. Pure: returns a new Report, never mutates the input. */
export const applyServerAnalysis = (
  report: Report,
  a: ServerAnalysis | null | undefined,
  survey: SurveyAnswers,
  currentYear: number = thisYear(),
): Report => {
  if (!a) return report;
  const r: Report = { ...report };
  let changed = false;
  if (a.f3_hz || a.f4_hz) {
    r.f3 = a.f3_hz ?? null;
    r.f4 = a.f4_hz ?? null;
    changed = true;
  }
  if (a.register_chest != null) {
    r.registers = {
      chest: a.register_chest,
      mix: a.register_mix ?? 0,
      head: a.register_head ?? 0,
    };
    changed = true;
  }
  if (
    a.voice_character &&
    (CHARACTER_KEYS as readonly string[]).includes(a.voice_character)
  ) {
    r.character = a.voice_character as CharacterKey;
    changed = true;
  }
  if (a.voice_quality != null && survey.birthYear) {
    const actual = currentYear - Number(survey.birthYear);
    const voiceAge = Math.max(
      14,
      Math.min(70, Math.round(actual + (50 - a.voice_quality) / 8)),
    );
    r.vocalAge = { voiceAge, actual };
    changed = true;
  }
  if (mergePrecisionScalars(r, a)) changed = true;
  if (mergePlacement(r, a)) changed = true;
  r._serverPrecise = changed;
  return r;
};
